import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Per-route <link rel="canonical"> and <meta name="robots"> for the CLIENT-RENDERED routes.
//
// WHY: only the prerendered routes (/m/**, /updates/**, /question-papers, /strategy-guides,
// /mentor-signup) get server-side head tags. Everything else — the homepage, /about,
// /success-stories, every dashboard/admin screen, and every URL that matches no route at all
// — was served the identical static shell with NO canonical and the homepage's og:url. To a
// crawler that reads as unlimited near-duplicate pages, which is exactly what "Crawled –
// currently not indexed" is made of. Google executes JS before indexing, so setting these in
// the DOM works; the prerendered routes overwrite their own tags with identical values.
//
// Anything the prerenders already own is left alone: they strip and re-inject canonical
// server-side, and this only ever sets a self-referential URL for the same path.
const SITE = 'https://shreyaadieducation.in';

// Signed-in application surface. Real pages, but nothing a search result should ever land on
// — and each one renders the same empty shell to a crawler.
const PRIVATE_PREFIXES = [
  '/dashboard', '/admin', '/learner', '/signup', '/kyc', '/health',
  '/r/confirm', '/mentor-unsubscribe', '/updates-unsubscribe',
];

/** Canonical URL for a path: origin + path, no query/hash, no trailing slash (except root). */
export function canonicalFor(pathname: string): string {
  // /handbook renders the SAME component as / — byte-identical HTML at two URLs. Point it
  // at the homepage rather than letting Google pick a winner between them.
  if (pathname === '/handbook') return `${SITE}/`;
  const clean = pathname.length > 1 ? pathname.replace(/\/+$/, '') : '/';
  return `${SITE}${clean}`;
}

export function isPrivatePath(pathname: string): boolean {
  return PRIVATE_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

// The URL the browser actually loaded. On a prerendered route that document's canonical was
// written by the server, which sometimes knows better than the path does — announcementPrerender
// points several crawlable variants at /updates on purpose. Overwriting that with a
// self-referential URL would undo the consolidation it exists to create.
const INITIAL_PATH = typeof window !== 'undefined' ? window.location.pathname : '/';

/**
 * Should the client own the canonical tag for this route?
 *
 * No, only while we are still on the server-rendered document AND the server shipped a
 * canonical of its own. After any client navigation the server's tag describes the previous
 * page, so it must be replaced.
 */
export function shouldSetCanonical(pathname: string, hasServerCanonical: boolean, initialPath = INITIAL_PATH): boolean {
  return !(hasServerCanonical && pathname === initialPath);
}

function upsertLink(rel: string, href: string): void {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
  el.dataset.routeMeta = 'true';
}

function setRobots(content: string | null): void {
  const el = document.head.querySelector<HTMLMetaElement>('meta[name="robots"]');
  if (content === null) {
    // A prerendered page may have shipped its own noindex (e.g. an empty exam×city landing).
    // Only remove tags this component added, never the server's.
    if (el?.dataset.routeMeta === 'true') el.remove();
    return;
  }
  if (el) { el.content = content; el.dataset.routeMeta = 'true'; return; }
  const meta = document.createElement('meta');
  meta.name = 'robots';
  meta.content = content;
  meta.dataset.routeMeta = 'true';
  document.head.appendChild(meta);
}

/**
 * Keeps the canonical + robots tags in step with the current route. `notFound` is set by the
 * catch-all route so an unmatched URL is explicitly noindex instead of a 200-OK duplicate of
 * the shell (Firebase Hosting rewrites `**` to index.html, so there is no real 404 status to
 * lean on).
 */
export function RouteMeta({ notFound = false }: { notFound?: boolean }) {
  const { pathname } = useLocation();
  useEffect(() => {
    const existing = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    const serverOwned = !!existing && existing.dataset.routeMeta !== 'true';
    if (shouldSetCanonical(pathname, serverOwned)) upsertLink('canonical', canonicalFor(pathname));
    setRobots(notFound || isPrivatePath(pathname) ? 'noindex,follow' : null);
  }, [pathname, notFound]);
  return null;
}
